import type { RootState } from "../../reducers";
import type { Credentials, S3Bucket } from "modules/objectStorage/types";
import type {
  BackupStorageContext,
  BackupStorageKeyContext,
  BackupBucketCreateParams,
  BackupStoragePicker
} from "./types";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading
} from "redux-saga/effects";
import { isAxiosError } from "typeGuards/isAxiosError";
import { appConfig } from "../../appConfig";
import { axiosInstance } from "../../axios";
import {
  DATABASE_METRIC_DEFAULT_DURATION,
  DATABASE_METRIC_DEFAULT_RANGE
} from "./constants";
import { getDatabaseErrorMessage } from "./errors";
import { keptInstalledNote, removedExtensions } from "./extensions";
import { databasesActions } from "./slices";
import {
  ChangeDatabaseStatusParams,
  ChangeDatabaseStatusResponse,
  CreateBackupDatabaseParams,
  CreateBackupDatabaseResponse,
  CreateDatabaseChildParams,
  CreateDatabaseChildResponse,
  CreateDatabaseParams,
  CreateDatabaseResponse,
  CreateDatabaseUserParams,
  CreateDatabaseUserResponse,
  CreateReplicaDatabaseParams,
  CreateReplicaDatabaseResponse,
  DatabaseChild,
  CronBackupParams,
  CronBackupResponse,
  CronDeleteParams,
  CronDeleteResponse,
  DeleteDatabaseChildParams,
  DeleteDatabaseParams,
  DeleteDatabaseResponse,
  DeleteDatabaseUserParams,
  GetDBServiceParamsParams,
  GetDBServiceParamsResponse,
  GetDBServicesParamsParams,
  GetDBServicesParamsResponse,
  GetDatabaseBackupStatusResponse,
  GetDatabaseChildrenParams,
  GetDatabaseChildrenResponse,
  GetDatabaseCustomOptionsParams,
  GetDatabaseCustomOptionsResponse,
  GetDatabaseEncodingsParams,
  GetDatabaseEncodingsResponse,
  GetDatabaseMetricsParams,
  GetDatabaseMetricsResponse,
  GetDatabaseParams,
  GetDatabaseResponse,
  GetDatabaseUsersParams,
  GetDatabaseUsersResponse,
  GetDatabasesParams,
  GetDatabasesResponse,
  GetDbaasQuotasParams,
  GetDbaasQuotasResponse,
  GetManagementUrlParams,
  GetManagementUrlResponse,
  GetReplicaDatabaseParams,
  GetReplicaDatabasesResponse,
  ResetDatabasePasswordParams,
  ResetDatabasePasswordResponse,
  ResetDatabaseUserPasswordParams,
  ResetDatabaseUserPasswordResponse,
  BackupListGrouped,
  RestoreDatabaseParams,
  RestoreDatabaseResponse,
  RollbackRestoreParams,
  RollbackRestoreResponse,
  S3LinkDatabaseParams,
  S3LinkDatabaseResponse,
  S3UnlinkDatabaseParams,
  S3UnlinkDatabaseResponse,
  SetDatabaseUserPermissionsParams,
  UpdateDatabaseChildParams,
  UpdateDatabaseCustomOptionsParams,
  UpdateDatabaseCustomOptionsResponse,
  UpdateDatabaseParams,
  UpdateDatabaseResponse
} from "./types";

const serviceApiPath = `gotham-golem/method/api/v1/`;

// A polled read that keeps failing the same way reports it once, not on every tick.
const reportedReadFailures = new Map<string, string>();

function* notifyReadFailureSaga(
  action: PayloadAction<unknown>,
  title: string,
  e: unknown
): SagaIterator<void> {
  const text = getDatabaseErrorMessage(e);
  if (reportedReadFailures.get(action.type) === text) {
    return;
  }
  reportedReadFailures.set(action.type, text);
  yield put(
    notificationsActions.showNotification({
      title,
      text,
      type: NOTIFICATION_TYPES.ERROR
    })
  );
}

export function* getDatabaseSaga(
  action: PayloadAction<GetDatabaseParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabaseSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(notifyReadFailureSaga, action, "Failed to get database data", e);
  }
}

export function* getDatabasesSaga(
  action: PayloadAction<GetDatabasesParams>
): SagaIterator<void> {
  try {
    const { orgId, projId } = action.payload;
    const response: AxiosResponse<GetDatabasesResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}list-all/${orgId}/${projId}`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabasesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabasesFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get databases data",
      e
    );
  }
}

export function* getDatabaseCustomOptionsSaga(
  action: PayloadAction<GetDatabaseCustomOptionsParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseCustomOptionsResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/custom-parameters`
      );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        databasesActions.getDatabaseCustomOptionsSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseCustomOptionsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database custom options data",
      e
    );
  }
}

export function* getDatabaseEncodingsSaga(
  action: PayloadAction<GetDatabaseEncodingsParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseEncodingsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/encodings`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabaseEncodingsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseEncodingsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database encodings data",
      e
    );
  }
}

export function* updateDatabaseCustomOptionsSaga(
  action: PayloadAction<UpdateDatabaseCustomOptionsParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id, data, unset } = action.payload;
    yield put(
      notificationsActions.showNotification({
        title: "Database custom option is updating.",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const url = `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/custom-parameters`;
    const response: AxiosResponse<UpdateDatabaseCustomOptionsResponse> = unset
      ? yield call(axiosInstance.delete, `${url}/${encodeURIComponent(unset)}`)
      : yield call(axiosInstance.put, url, data);

    yield put(
      databasesActions.updateDatabaseCustomOptionsSuccess(response.data)
    );
    yield put(
      databasesActions.getDatabaseCustomOptionsRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database custom option saved; applying.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    const message =
      getDatabaseErrorMessage(e) ||
      "Unable to save the parameter. Please try again.";
    yield put(
      databasesActions.updateDatabaseCustomOptionsFailure({
        params: action.payload,
        error: message
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update database custom options",
        text: message,
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createReplicaDatabaseSaga(
  action: PayloadAction<CreateReplicaDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating database replica...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<CreateReplicaDatabaseResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/replicas`,
      data
    );
    yield put(databasesActions.createReplicaDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Replica database has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.createReplicaDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create database replica",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getReplicaDatabaseSaga(
  action: PayloadAction<GetReplicaDatabaseParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetReplicaDatabasesResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/replicas`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getReplicaDatabasesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getReplicaDatabasesFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get replica database data",
      e
    );
  }
}

export function* getDbaasQuotasSaga(
  action: PayloadAction<GetDbaasQuotasParams>
): SagaIterator<void> {
  if (appConfig.isDbaasEnabled) {
    try {
      const { serviceName, orgId, projId } = action.payload;
      const response: AxiosResponse<GetDbaasQuotasResponse> = yield call(
        axiosInstance.get,
        `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/quotas`
      );
      reportedReadFailures.delete(action.type);
      const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
      if (!isPollStopped) {
        yield put(databasesActions.getDbaasQuotasSuccess(response.data));
      }
    } catch (e) {
      yield put(
        databasesActions.getDbaasQuotasFailure({
          params: action.payload,
          error: getDatabaseErrorMessage(e)
        })
      );
      yield call(
        notifyReadFailureSaga,
        action,
        "Failed to get dbaas quotas data",
        e
      );
    }
  }
}

export function* createDatabaseSaga(
  action: PayloadAction<CreateDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, data } = action.payload;
    const response: AxiosResponse<CreateDatabaseResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}`,
      data
    );
    yield put(databasesActions.createDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.createDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateDatabaseSaga(
  action: PayloadAction<UpdateDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<UpdateDatabaseResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}`,
      data
    );
    yield put(databasesActions.updateDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.updateDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteDatabaseSaga(
  action: PayloadAction<DeleteDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<DeleteDatabaseResponse> = yield call(
      axiosInstance.delete,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}`
    );
    yield put(databasesActions.deleteDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title:
          "Database has been successfully deleted. It might take a minute until all resources are removed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.deleteDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getDBServiceParamsSaga(
  action: PayloadAction<GetDBServiceParamsParams>
): SagaIterator<void> {
  try {
    const { serviceName } = action.payload;
    const response: AxiosResponse<GetDBServiceParamsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/params`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDBServiceParamsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDBServiceParamsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get available service parameters",
      e
    );
  }
}

export function* getDBServicesParamsSaga(
  action: PayloadAction<GetDBServicesParamsParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetDBServicesParamsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}all-params`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDBServicesParamsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDBServicesParamsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get available service parameters",
      e
    );
  }
}

export function* changeDatabaseStatusSaga(
  action: PayloadAction<ChangeDatabaseStatusParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Changing database status...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, type } = action.payload;
    const response: AxiosResponse<ChangeDatabaseStatusResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/actions/change-status`,
      null,
      {
        params: {
          type
        }
      }
    );
    yield put(databasesActions.changeDatabaseStatusSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title:
          "Status of database has been successfully changed. It might take a minute until the process will be finished.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.changeDatabaseStatusFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to change database status",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getManagementUrlSaga(
  action: PayloadAction<GetManagementUrlParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Getting Management URL...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetManagementUrlResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/mgr`
    );
    yield put(databasesActions.getManagementUrlSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Management URL has been successfully received.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
    window.open(response.data, "_blank");
  } catch (e) {
    yield put(
      databasesActions.getManagementUrlFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get Management URL",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getDatabaseBackupStatusSaga(
  action: PayloadAction<GetDatabaseParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseBackupStatusResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/status`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabaseBackupStatusSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseBackupStatusFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database backup status",
      e
    );
  }
}

export function* getDatabaseBackupFilesSaga(
  action: PayloadAction<GetDatabaseParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    // group=set returns the flat listing plus the backup runs derived from it
    const response: AxiosResponse<BackupListGrouped> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups?group=set`
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabaseBackupFilesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseBackupFilesFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database backup files",
      e
    );
  }
}

export function* rollbackRestoreSaga(
  action: PayloadAction<RollbackRestoreParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Undoing the last restore...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    // No body: the API restores only the pre-restore copy recorded in the status
    const response: AxiosResponse<RollbackRestoreResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/rollback`
    );
    yield put(databasesActions.rollbackRestoreSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Undoing the restore has been started.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.rollbackRestoreFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to undo the restore",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* restoreDatabaseSaga(
  action: PayloadAction<RestoreDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Starting database restoring...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<RestoreDatabaseResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/restore`,
      data
    );
    yield put(databasesActions.restoreDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database restoring has been started.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.restoreDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to start database restoring",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* cronBackupSaga(
  action: PayloadAction<CronBackupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Backup scheduler applying...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<CronBackupResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/cron`,
      data
    );
    yield put(databasesActions.cronBackupSuccess(response.data));
    yield put(
      databasesActions.getDatabaseBackupStatusRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Backup scheduler applied.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.cronBackupFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to apply scheduler configuration",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* cronDeleteSaga(
  action: PayloadAction<CronDeleteParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Backup scheduler deleting...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<CronDeleteResponse> = yield call(
      axiosInstance.delete,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/cron`
    );
    yield put(databasesActions.cronDeleteSuccess(response.data));
    yield put(
      databasesActions.getDatabaseBackupStatusRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Backup scheduler deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.cronDeleteFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete scheduler configuration.",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* backupDatabaseSaga(
  action: PayloadAction<CreateBackupDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Starting the database backing up...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<CreateBackupDatabaseResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups`,
      data
    );
    yield put(databasesActions.backupDatabaseSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database backing up has been started.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.backupDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to start database backing up",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* s3LinkDatabaseSaga(
  action: PayloadAction<S3LinkDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Linking database to S3 storage...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<S3LinkDatabaseResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/link`,
      data
    );
    yield put(databasesActions.s3LinkDatabaseSuccess(response.data));
    yield put(databasesActions.clearBackupFiles());
    yield put(
      databasesActions.getDatabaseBackupFilesRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      databasesActions.getDatabaseBackupStatusRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database has been successfully linked to S3 storage.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.s3LinkDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to link to S3 storage",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* s3UnlinkDatabaseSaga(
  action: PayloadAction<S3UnlinkDatabaseParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Unlinking database from S3 storage...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<S3UnlinkDatabaseResponse> = yield call(
      axiosInstance.delete,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/backups/link`
    );
    yield put(databasesActions.s3UnlinkDatabaseSuccess(response.data));
    yield put(databasesActions.clearBackupFiles());
    yield put(
      databasesActions.getDatabaseBackupStatusRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database has been successfully unlinked from S3 storage.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.s3UnlinkDatabaseFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to unlink from S3 storage",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getDatabaseChildrenSaga(
  action: PayloadAction<GetDatabaseChildrenParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseChildrenResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/db`
    );
    reportedReadFailures.delete(action.type);
    yield put(databasesActions.getDatabaseChildrenSuccess(response.data));
  } catch (e) {
    yield put(
      databasesActions.getDatabaseChildrenFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database children",
      e
    );
  }
}

export function* getDatabaseUsersSaga(
  action: PayloadAction<GetDatabaseUsersParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<GetDatabaseUsersResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/users`
    );
    reportedReadFailures.delete(action.type);
    yield put(databasesActions.getDatabaseUsersSuccess(response.data));
  } catch (e) {
    yield put(
      databasesActions.getDatabaseUsersFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield call(
      notifyReadFailureSaga,
      action,
      "Failed to get database users",
      e
    );
  }
}

export function* createDatabaseChildSaga(
  action: PayloadAction<CreateDatabaseChildParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<CreateDatabaseChildResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/db`,
      data
    );
    yield put(databasesActions.createDatabaseChildSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database created successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.createDatabaseChildFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createDatabaseUserSaga(
  action: PayloadAction<CreateDatabaseUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the database user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, data } = action.payload;
    const response: AxiosResponse<CreateDatabaseUserResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/users`,
      data
    );
    yield put(databasesActions.createDatabaseUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Database user created successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.createDatabaseUserFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create database user",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteDatabaseChildSaga(
  action: PayloadAction<DeleteDatabaseChildParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, dbName } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/db/${dbName}`
    );
    yield put(
      databasesActions.deleteDatabaseChildSuccess({ params: action.payload })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database deleted successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.deleteDatabaseChildFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateDatabaseChildSaga(
  action: PayloadAction<UpdateDatabaseChildParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the database...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, dbName, extensions, settings } =
      action.payload;
    const children: DatabaseChild[] | null = yield select(
      (state: RootState) => state.databases.databaseChildren
    );
    const kept =
      extensions &&
      keptInstalledNote(
        removedExtensions(
          children?.find((child) => child.name === dbName)?.extensions,
          extensions
        )
      );
    yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/db/${dbName}`,
      {
        ...(extensions && { extensions }),
        ...(settings && { settings })
      }
    );
    yield put(
      databasesActions.updateDatabaseChildSuccess({ params: action.payload })
    );
    // The API normalises what it was sent, so the list is read back
    yield put(
      databasesActions.getDatabaseChildrenRequest({
        serviceName,
        orgId,
        projId,
        id
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database updated",
        ...(kept && { text: kept }),
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.updateDatabaseChildFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update database",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteDatabaseUserSaga(
  action: PayloadAction<DeleteDatabaseUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the database user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, username } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/users/${username}`
    );
    yield put(
      databasesActions.deleteDatabaseUserSuccess({ params: action.payload })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Database user deleted successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.deleteDatabaseUserFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete database user",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* setDatabaseUserPermissionsSaga(
  action: PayloadAction<SetDatabaseUserPermissionsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating user permissions...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, username, permissions } =
      action.payload;
    yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/users/${username}`,
      permissions
    );
    yield put(databasesActions.setDatabaseUserPermissionsSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "User permissions updated successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.setDatabaseUserPermissionsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update user permissions",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* resetDatabaseUserPasswordSaga(
  action: PayloadAction<ResetDatabaseUserPasswordParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Resetting the user password...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id, username } = action.payload;
    const response: AxiosResponse<ResetDatabaseUserPasswordResponse> =
      yield call(
        axiosInstance.put,
        `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/users/${username}/pwd-reset`
      );
    yield put(databasesActions.resetDatabaseUserPasswordSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "User password reset successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.resetDatabaseUserPasswordFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to reset user password",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* resetDatabasePasswordSaga(
  action: PayloadAction<ResetDatabasePasswordParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Resetting the password...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { serviceName, orgId, projId, id } = action.payload;
    const response: AxiosResponse<ResetDatabasePasswordResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/pwd-reset`
    );
    yield put(databasesActions.resetDatabasePasswordSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Password reset successfully",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      databasesActions.resetDatabasePasswordFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to reset password",
        text: getDatabaseErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getDatabaseMetricsSaga(
  action: PayloadAction<GetDatabaseMetricsParams>
): SagaIterator<void> {
  try {
    const { serviceName, orgId, projId, id, duration, range } = action.payload;
    const response: AxiosResponse<GetDatabaseMetricsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}dbaas/${serviceName}/db/${orgId}/${projId}/${id}/metrics`,
      {
        params: {
          duration: duration ?? DATABASE_METRIC_DEFAULT_DURATION,
          range: range ?? DATABASE_METRIC_DEFAULT_RANGE
        },
        "axios-retry": { retries: 0 }
      }
    );
    reportedReadFailures.delete(action.type);
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(databasesActions.getDatabaseMetricsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      databasesActions.getDatabaseMetricsFailure({
        params: action.payload,
        error: getDatabaseErrorMessage(e)
      })
    );
    // 404: Prometheus has no series for the database yet
    if (isAxiosError(e) && e.response?.status === 404) {
      reportedReadFailures.delete(action.type);
    } else {
      yield call(
        notifyReadFailureSaga,
        action,
        "Failed to get database metrics",
        e
      );
    }
  }
}

function* backupPickerActive(
  context: BackupStorageContext & { credsId?: string }
): SagaIterator<boolean> {
  const picker: BackupStoragePicker = yield select(
    (state: RootState) => state.databases.backupStoragePicker
  );
  return (
    picker.context?.sessionId === context.sessionId &&
    picker.context.regionId === context.regionId &&
    picker.context.projId === context.projId &&
    (!context.credsId || picker.credentialId === context.credsId)
  );
}

// These requests belong only to the database backup picker. Object-storage
// screens retain their own state, polling and takeLeading watchers unchanged.
function* backupStorageKeysSaga(
  action: PayloadAction<BackupStorageContext>
): SagaIterator<void> {
  const context = action.payload;
  const active: boolean = yield call(backupPickerActive, context);
  if (!active) return;
  try {
    const response: AxiosResponse<Credentials[]> = yield call(
      axiosInstance.get,
      `gotham-${context.regionId}-identity/method/${context.projId}/creds`
    );
    yield put(
      databasesActions.backupStorageKeysSuccess({
        context,
        keys: response.data
      })
    );
  } catch (e) {
    yield put(
      databasesActions.backupStorageKeysFailure({
        context,
        error: getDatabaseErrorMessage(e)
      })
    );
  }
}
function* backupStorageKeySaga(
  action: PayloadAction<BackupStorageKeyContext>
): SagaIterator<void> {
  const context = action.payload;
  const active: boolean = yield call(backupPickerActive, context);
  if (!active) return;
  try {
    const response: AxiosResponse<Credentials> = yield call(
      axiosInstance.get,
      `gotham-${context.regionId}-identity/method/${context.projId}/creds/${context.credsId}`
    );
    yield put(
      databasesActions.backupStorageKeySuccess({ context, key: response.data })
    );
  } catch (e) {
    yield put(
      databasesActions.backupStorageKeyFailure({
        context,
        error: getDatabaseErrorMessage(e)
      })
    );
  }
}
function* backupStorageBucketsSaga(
  action: PayloadAction<BackupStorageKeyContext>
): SagaIterator<void> {
  const context = action.payload;
  const active: boolean = yield call(backupPickerActive, context);
  if (!active) return;
  try {
    const response: AxiosResponse<S3Bucket[]> = yield call(
      axiosInstance.get,
      `gotham-${context.regionId}-object-storage/method/${context.projId}/credentials/${context.credsId}/buckets`
    );
    yield put(
      databasesActions.backupStorageBucketsSuccess({
        context,
        buckets: response.data
      })
    );
  } catch (e) {
    yield put(
      databasesActions.backupStorageBucketsFailure({
        context,
        error: getDatabaseErrorMessage(e)
      })
    );
  }
}
function* createBackupStorageKeySaga(
  action: PayloadAction<BackupStorageContext>
): SagaIterator<void> {
  const context = action.payload;
  const active: boolean = yield call(backupPickerActive, context);
  if (!active) return;
  try {
    const response: AxiosResponse<Credentials> = yield call(
      axiosInstance.post,
      `gotham-${context.regionId}-identity/method/${context.projId}/creds`
    );
    yield put(
      databasesActions.createBackupStorageKeySuccess({
        context,
        key: response.data
      })
    );
    const stillActive: boolean = yield call(backupPickerActive, context);
    if (stillActive)
      yield put(databasesActions.backupStorageKeysRequest(context));
  } catch (e) {
    yield put(
      databasesActions.createBackupStorageKeyFailure({
        context,
        error: getDatabaseErrorMessage(e)
      })
    );
  }
}
function* createBackupStorageBucketSaga(
  action: PayloadAction<BackupBucketCreateParams>
): SagaIterator<void> {
  const context = action.payload;
  const active: boolean = yield call(backupPickerActive, context);
  if (!active) return;
  try {
    yield call(
      axiosInstance.post,
      `gotham-${context.regionId}-object-storage/method/${context.projId}/credentials/${context.credsId}/bucket/${context.name}`
    );
    yield put(databasesActions.createBackupStorageBucketSuccess(context));
    const stillActive: boolean = yield call(backupPickerActive, context);
    if (stillActive)
      yield put(databasesActions.backupStorageBucketsRequest(context));
  } catch (e) {
    yield put(
      databasesActions.createBackupStorageBucketFailure({
        context,
        error: getDatabaseErrorMessage(e)
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLatest(
      databasesActions.backupStorageKeysRequest,
      backupStorageKeysSaga
    ),
    takeLatest(databasesActions.backupStorageKeyRequest, backupStorageKeySaga),
    takeLatest(
      databasesActions.backupStorageBucketsRequest,
      backupStorageBucketsSaga
    ),
    takeEvery(
      databasesActions.createBackupStorageKeyRequest,
      createBackupStorageKeySaga
    ),
    takeEvery(
      databasesActions.createBackupStorageBucketRequest,
      createBackupStorageBucketSaga
    ),
    takeLeading(databasesActions.getDatabaseRequest, getDatabaseSaga),
    takeLeading(
      databasesActions.getDatabaseMetricsRequest,
      getDatabaseMetricsSaga
    ),
    takeEvery(
      databasesActions.resetDatabasePasswordRequest,
      resetDatabasePasswordSaga
    ),
    takeLeading(databasesActions.getDatabasesRequest, getDatabasesSaga),
    takeLeading(
      databasesActions.getReplicaDatabasesRequest,
      getReplicaDatabaseSaga
    ),
    takeLatest(
      databasesActions.getDatabaseBackupStatusRequest,
      getDatabaseBackupStatusSaga
    ),
    takeLatest(
      databasesActions.getDatabaseBackupFilesRequest,
      getDatabaseBackupFilesSaga
    ),
    takeEvery(
      databasesActions.getDatabaseEncodingsRequest,
      getDatabaseEncodingsSaga
    ),
    takeLatest(
      databasesActions.getDatabaseCustomOptionsRequest,
      getDatabaseCustomOptionsSaga
    ),
    takeEvery(databasesActions.createDatabaseRequest, createDatabaseSaga),
    takeEvery(
      databasesActions.createReplicaDatabaseRequest,
      createReplicaDatabaseSaga
    ),
    takeEvery(databasesActions.updateDatabaseRequest, updateDatabaseSaga),
    takeEvery(
      databasesActions.updateDatabaseCustomOptionsRequest,
      updateDatabaseCustomOptionsSaga
    ),
    takeEvery(databasesActions.deleteDatabaseRequest, deleteDatabaseSaga),
    takeEvery(databasesActions.getManagementUrlRequest, getManagementUrlSaga),
    takeEvery(databasesActions.restoreDatabaseRequest, restoreDatabaseSaga),
    takeEvery(databasesActions.rollbackRestoreRequest, rollbackRestoreSaga),
    takeEvery(databasesActions.cronBackupRequest, cronBackupSaga),
    takeEvery(databasesActions.cronDeleteRequest, cronDeleteSaga),
    takeEvery(databasesActions.backupDatabaseRequest, backupDatabaseSaga),
    takeLeading(databasesActions.s3LinkDatabaseRequest, s3LinkDatabaseSaga),
    takeLeading(databasesActions.s3UnlinkDatabaseRequest, s3UnlinkDatabaseSaga),
    takeLeading(
      databasesActions.getDBServiceParamsRequest,
      getDBServiceParamsSaga
    ),
    takeLeading(databasesActions.getDbaasQuotasRequest, getDbaasQuotasSaga),
    takeLeading(
      databasesActions.getDBServicesParamsRequest,
      getDBServicesParamsSaga
    ),
    takeEvery(
      databasesActions.changeDatabaseStatusRequest,
      changeDatabaseStatusSaga
    ),
    takeLatest(
      databasesActions.getDatabaseChildrenRequest,
      getDatabaseChildrenSaga
    ),
    takeLeading(databasesActions.getDatabaseUsersRequest, getDatabaseUsersSaga),
    takeEvery(
      databasesActions.createDatabaseChildRequest,
      createDatabaseChildSaga
    ),
    takeEvery(
      databasesActions.createDatabaseUserRequest,
      createDatabaseUserSaga
    ),
    takeEvery(
      databasesActions.deleteDatabaseChildRequest,
      deleteDatabaseChildSaga
    ),
    takeEvery(
      databasesActions.updateDatabaseChildRequest,
      updateDatabaseChildSaga
    ),
    takeEvery(
      databasesActions.deleteDatabaseUserRequest,
      deleteDatabaseUserSaga
    ),
    takeEvery(
      databasesActions.setDatabaseUserPermissionsRequest,
      setDatabaseUserPermissionsSaga
    ),
    takeEvery(
      databasesActions.resetDatabaseUserPasswordRequest,
      resetDatabaseUserPasswordSaga
    )
  ]);
}
